<template>
  <div class="cd-header">
    <div class="cd-header-left">
      <h4 class="cd-title">Dashboard check-in</h4>
      <div class="cd-freshness" :class="freshnessClass">
        <span class="cd-pulse-dot"></span>
        {{ freshnessLabel }}
      </div>
    </div>

    <div class="cd-header-right">
      <DateRangePicker
        :period="filters.period"
        :from="filters.from"
        :to="filters.to"
        :active-dates="filterOptions.active_dates || []"
        :event="filterOptions.event || null"
        @change="onPeriodChange"
      />

      <div class="cd-divider"></div>

      <TagFilterChips
        v-if="filterOptions.tags && filterOptions.tags.length > 0"
        :tags="filterOptions.tags"
        :selected-ids="filters.tagIds || []"
        @change="onTagsChange"
      />

      <button
        type="button"
        class="cd-refresh-btn"
        :class="{ 'cd-refresh-btn-spinning': isLoading }"
        :disabled="isLoading"
        @click="$emit('refresh')"
        title="Atualizar agora"
      >
        <i class="bx bx-revision"></i>
      </button>

      <button
        type="button"
        class="cd-reports-btn"
        @click="$emit('open-reports')"
        title="Exportar relatório"
      >
        <i class="bx bx-bar-chart-alt-2"></i>
        <span>Relatórios</span>
      </button>
    </div>
  </div>
</template>

<script>
import DateRangePicker from './DateRangePicker.vue'
import TagFilterChips from './TagFilterChips.vue'

export default {
  name: 'dashboard-header',
  components: { DateRangePicker, TagFilterChips },
  props: {
    filters: { type: Object, required: true },
    filterOptions: { type: Object, required: true },
    isLoading: { type: Boolean, default: false },
    lastUpdate: { type: Date, default: null }
  },
  data: () => ({
    // Relógio interno que atualiza a label de freshness a cada segundo,
    // sem causar re-render da página inteira (só dessa string).
    tick: 0,
    tickInterval: null
  }),
  computed: {
    secondsSinceUpdate () {
      // eslint-disable-next-line no-unused-expressions
      this.tick // força re-cálculo
      if (!this.lastUpdate) return null
      return Math.floor((Date.now() - this.lastUpdate.getTime()) / 1000)
    },
    freshnessLabel () {
      if (this.isLoading && !this.lastUpdate) return 'carregando…'
      if (this.secondsSinceUpdate === null) return '—'
      const s = this.secondsSinceUpdate
      if (s < 5) return 'ao vivo'
      if (s < 60) return `atualizado há ${s}s`
      const m = Math.floor(s / 60)
      return `atualizado há ${m}min`
    },
    freshnessClass () {
      const s = this.secondsSinceUpdate
      if (s === null) return 'cd-fresh-idle'
      if (s < 30) return 'cd-fresh-live'
      if (s < 90) return 'cd-fresh-ok'
      return 'cd-fresh-stale'
    }
  },
  mounted () {
    this.tickInterval = setInterval(() => { this.tick++ }, 1000)
  },
  beforeDestroy () {
    if (this.tickInterval) clearInterval(this.tickInterval)
  },
  methods: {
    /**
     * Recebe payload do DateRangePicker e propaga ao parent.
     * Estrutura: { period, from?, to? } — onde from/to vêm pra modos
     * 'single_day' e 'custom'.
     */
    onPeriodChange (payload) {
      this.$emit('change-filters', payload)
    },
    onTagsChange (ids) {
      this.$emit('change-filters', { tagIds: ids })
    }
  }
}
</script>

<style lang="scss" scoped>
.cd-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.cd-header-left {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.cd-title {
  font-size: 20px;
  font-weight: 600;
  color: #1a1d26;
  margin: 0;
}

.cd-freshness {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 500;
  color: #8b90a5;

  .cd-pulse-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #b0b5c8;
    box-shadow: 0 0 0 0 rgba(176, 181, 200, 0);
  }

  &.cd-fresh-live {
    color: #34c38f;
    .cd-pulse-dot {
      background: #34c38f;
      animation: cd-pulse 1.6s ease-out infinite;
    }
  }
  &.cd-fresh-ok {
    color: #4a5068;
    .cd-pulse-dot { background: #556ee6; }
  }
  &.cd-fresh-stale {
    color: #f1b44c;
    .cd-pulse-dot { background: #f1b44c; }
  }
}

@keyframes cd-pulse {
  0%   { box-shadow: 0 0 0 0 rgba(52, 195, 143, 0.55); }
  70%  { box-shadow: 0 0 0 7px rgba(52, 195, 143, 0); }
  100% { box-shadow: 0 0 0 0 rgba(52, 195, 143, 0); }
}

.cd-header-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.cd-period-chips {
  display: inline-flex;
  background: #fff;
  border: 1px solid #eaecf2;
  border-radius: 8px;
  padding: 3px;
  gap: 2px;
}

.cd-chip {
  background: transparent;
  border: 0;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  color: #4a5068;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover { background: #f5f6fa; }

  &.cd-chip-active {
    background: #556ee6;
    color: #fff;
    box-shadow: 0 2px 8px rgba(85, 110, 230, 0.3);
  }
}

.cd-divider {
  width: 1px;
  height: 24px;
  background: #eaecf2;
}

.cd-refresh-btn {
  width: 38px;
  height: 38px;
  display: grid;
  place-items: center;
  background: #fff;
  border: 1px solid #eaecf2;
  border-radius: 8px;
  color: #4a5068;
  cursor: pointer;
  transition: all 0.15s ease;
  font-size: 18px;

  &:hover { background: #f5f6fa; color: #556ee6; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }

  &.cd-refresh-btn-spinning i { animation: cd-spin 0.8s linear infinite; }
}

.cd-reports-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 38px;
  padding: 0 14px;
  background: #556ee6;
  border: 1px solid #556ee6;
  border-radius: 8px;
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  box-shadow: 0 2px 8px rgba(85, 110, 230, 0.25);

  i { font-size: 16px; }

  &:hover { background: #4458c9; border-color: #4458c9; }
}

@keyframes cd-spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 768px) {
  .cd-header-right {
    width: 100%;
    .cd-divider { display: none; }
  }
}
</style>
